import React, { useEffect } from "react";
import { Button, Nav, Card, Spinner } from "react-bootstrap";
import { X, User, LogOut, Layout, Sun, Moon, Globe, AlertTriangle, Ticket } from "lucide-react";
import { useTranslation } from "react-i18next";
import LanguageSwitcher from "../common/languageSwitcher";
import useUserStore from "../../stores/useUserStore";

const Sidebar = ({
  user,
  isOpen,
  onClose,
  onNavigate,
  currentView,
  isSubscribed,
  onRegisterNotifications,
  layoutMode,
  onToggleLayout,
  routineCounts = [],
}) => {
  const { t } = useTranslation();
  const { user: userStore, initializeFromSession } = useUserStore();

  // Inicializar el store desde sessionStorage al montar
  useEffect(() => {
    initializeFromSession();
  }, [initializeFromSession]);

  if (!isOpen) return null;

  // Obtener el conteo de videos por timeRange
  const getCountByTimeRange = (timeRange) => {
    if (!Array.isArray(routineCounts)) return 0;
    const found = routineCounts.find((item) => item.timeRange === timeRange);
    return found ? found.totalVideos : 0;
  };

  return (
    <div
      className="position-fixed top-0 start-0 w-100 h-100 d-flex"
      style={{ zIndex: 1050 }}
    >
      <div
        className="d-flex flex-column"
        style={{
          background: "linear-gradient(to bottom, #9ca3af, #6b7280)",
          width: "18rem",
          height: "100vh",
        }}
      >
        {/* Header del usuario */}
        <div className="p-4">
          <div className="d-flex flex-column align-items-center mb-3">
            {userStore.image ? (
              <div
                style={{
                  width: "100%",
                  height: "120px",
                  borderRadius: "12px",
                  overflow: "hidden",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: "transparent",
                  marginBottom: "1rem",
                }}
              >
                <img
                  src={userStore.image}
                  alt="User"
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "contain",
                  }}
                />
              </div>
            ) : (
              <div
                className="bg-white d-flex align-items-center justify-content-center"
                style={{
                  width: "100%",
                  height: "120px",
                  borderRadius: "12px",
                  marginBottom: "1rem",
                }}
              >
                <User className="text-muted" size={48} />
              </div>
            )}
            <Button
              variant="link"
              className="text-white p-0 align-self-end"
              onClick={onClose}
              style={{
                position: "absolute",
                top: "1rem",
                right: "1rem",
              }}
            >
              <X size={24} />
            </Button>
          </div>

          <div className="text-white small mb-4 text-center">{user.email}</div>
        </div>

        {/* Menú de navegación */}
        <Nav className="flex-column flex-fill px-4">
          <Nav.Link
            onClick={() => onNavigate("routines")}
            className={`text-white py-3 px-0 ${
              currentView === "routines" ? "fw-medium" : ""
            }`}
            style={{
              textDecoration: "none",
              cursor: "pointer",
            }}
          >
            {t("move.routinesMenuLabel")}
          </Nav.Link>

          <Nav.Link
            onClick={() => onNavigate("metrics")}
            className={`text-white py-3 px-0 ${
              currentView === "metrics" ? "fw-medium" : ""
            }`}
            style={{
              textDecoration: "none",
              cursor: "pointer",
            }}
          >
            {t("move.metricsMenuLabel")}
          </Nav.Link>

          <Nav.Link
            onClick={() => onNavigate("report")}
            className={`text-white py-3 px-0 ${
              currentView === "report" ? "fw-medium" : ""
            }`}
            style={{
              textDecoration: "none",
              cursor: "pointer",
            }}
          >
            <AlertTriangle
              size={18}
              className="me-2"
              style={{ display: "inline" }}
            />
            {t("report.menuLabel")}
          </Nav.Link>

          <Nav.Link
            onClick={() => onNavigate("tickets")}
            className={`text-white py-3 px-0 ${
              currentView === "tickets" ? "fw-medium" : ""
            }`}
            style={{
              textDecoration: "none",
              cursor: "pointer",
            }}
          >
            <Ticket
              size={18}
              className="me-2"
              style={{ display: "inline" }}
            />
            {t("move.myTicketsMenuLabel")}
          </Nav.Link>

          {/* Cambiar vista de layout - Solo visible en vista de rutinas */}
          {currentView === "routines" && (
            <Nav.Link
              onClick={() => {
                onToggleLayout && onToggleLayout();
                onClose();
              }}
              className="text-white py-3 px-0"
              style={{
                textDecoration: "none",
                cursor: "pointer",
              }}
            >
              <Layout
                size={18}
                className="me-2"
                style={{ display: "inline" }}
              />
              {layoutMode === "horizontal"
                ? t("move.sideViewLabel")
                : t("move.horizontalViewLabel")}
            </Nav.Link>
          )}
        </Nav>

        {/* Notificaciones (aparecer solo si no está suscrito) */}
        <div className="px-4">
          {!isSubscribed && (
            <div
              onClick={() =>
                onRegisterNotifications && onRegisterNotifications()
              }
              className="text-white py-3 px-0"
              style={{ textDecoration: "none", cursor: "pointer" }}
            >
              {t("move.notificationsLabel")}
            </div>
          )}
        </div>

        {/* Card informativo de rutinas */}
        <div className="px-4 mb-3">
          <Card
            style={{
              backgroundColor: "#374151",
              border: "none",
              borderRadius: "12px",
            }}
          >
            <Card.Body className="p-3">
              <h6 className="text-white mb-3" style={{ fontSize: "0.9rem" }}>
                {t("move.routinesOfDay")}
              </h6>
              {routineCounts === null ? (
                <div className="d-flex justify-content-center py-2">
                  <Spinner animation="border" size="sm" variant="light" />
                </div>
              ) : (
                <div className="d-flex flex-column gap-2">
                  <div className="d-flex align-items-center justify-content-between text-white">
                    <div className="d-flex align-items-center gap-2">
                      <Sun size={16} className="text-warning" />
                      <span style={{ fontSize: "0.85rem" }}>
                        {t("move.morningLabel")}
                      </span>
                    </div>
                    <span
                      className="badge"
                      style={{
                        backgroundColor: "#10b981",
                        fontSize: "0.75rem",
                        padding: "4px 8px",
                      }}
                    >
                      {getCountByTimeRange(0)} {t("move.exercisesLabel")}
                    </span>
                  </div>
                  <div className="d-flex align-items-center justify-content-between text-white">
                    <div className="d-flex align-items-center gap-2">
                      <Moon size={16} className="text-info" />
                      <span style={{ fontSize: "0.85rem" }}>
                        {t("move.afternoonLabel")}
                      </span>
                    </div>
                    <span
                      className="badge"
                      style={{
                        backgroundColor: "#3b82f6",
                        fontSize: "0.75rem",
                        padding: "4px 8px",
                      }}
                    >
                      {getCountByTimeRange(1)} {t("move.exercisesLabel")}
                    </span>
                  </div>
                  <div className="d-flex align-items-center justify-content-between text-white">
                    <div className="d-flex align-items-center gap-2">
                      <Globe size={16} className="text-light" />
                      <span style={{ fontSize: "0.85rem" }}>
                        {t("move.mixedLabel")}
                      </span>
                    </div>
                    <span
                      className="badge"
                      style={{
                        backgroundColor: "#8b5cf6",
                        fontSize: "0.75rem",
                        padding: "4px 8px",
                      }}
                    >
                      {getCountByTimeRange(2)} {t("move.exercisesLabel")}
                    </span>
                  </div>
                </div>
              )}
            </Card.Body>
          </Card>
        </div>

        {/* Language Switcher */}
        <div className="px-4 mb-3 d-flex justify-content-center">
          <LanguageSwitcher variant="dark" />
        </div>

        {/* Logout */}
        <div className="p-4">
          <Button
            variant="light"
            className="w-100 py-3 px-4"
            onClick={() => onNavigate("logout")}
            style={{ borderRadius: "8px" }}
          >
            <LogOut size={18} className="me-2" />
            {t("move.logoutButton")}
          </Button>
        </div>
      </div>

      {/* Overlay */}
      <div
        className="flex-fill"
        style={{ backgroundColor: "rgba(0, 0, 0, 0.5)" }}
        onClick={onClose}
      />
    </div>
  );
};

export default React.memo(Sidebar);
